import Pusher, { Channel } from "pusher-js";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { envConfig } from "@/config";
import { RootStateType } from "types/store";
import { getPusherInstance } from "@/lib/pusherSingleton";

interface PusherContextType {
  pusher: Pusher | null;
  isConnected: boolean;
  subscribe: (channelName: string) => Channel | null;
  unsubscribe: (channelName: string) => void;
  toggleSubscription: (channelName: string, enabled: boolean) => Channel | null;
}

const PusherContext = createContext<PusherContextType | undefined>(undefined);

export const usePusher = () => {
  const context = useContext(PusherContext);
  if (!context) throw new Error("usePusher must be used inside PusherProvider");
  return context;
};

interface ChannelRef {
  channel: Channel;
  count: number;
}

export const PusherProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const tenantSlug = useSelector(
    (state: RootStateType) => state?.current?.customer?.tenantSlug
  );

  const channelsRef = useRef<Map<string, ChannelRef>>(new Map());

  const [pusher, setPusher] = useState<Pusher | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // ---------------- Initialization ----------------
  useEffect(() => {
    if (!envConfig.pusherKey || !tenantSlug) return;

    console.log(
      `%c[Pusher][Provider] Mount (tenant=${tenantSlug})`,
      "color: purple"
    );

    const instance = getPusherInstance();
    setPusher(instance);

    instance.connection.bind("state_change", (states: any) => {
      console.log(
        `%c[Pusher][Provider] Connection: ${states.current}`,
        "color: purple"
      );
      setIsConnected(states.current === "connected");
    });

    return () => {
      console.log(
        "%c[Pusher][Provider] Unmount (StrictMode safe)",
        "color: purple"
      );

      channelsRef.current.clear();
      setIsConnected(false);
      setPusher(null);
    };
  }, [tenantSlug]);

  // ---------------- Channel management ----------------
  const subscribe = useCallback(
    (channelName: string): Channel | null => {
      if (!pusher) return null;

      const map = channelsRef.current;
      const existing = map.get(channelName);

      if (existing) {
        existing.count++;
        console.log(
          `[Pusher][Channel] Reuse "${channelName}" (count=${existing.count})`
        );
        return existing.channel;
      }

      console.log(`[Pusher][Channel] SUBSCRIBE → ${channelName}`);
      const channel = pusher.subscribe(channelName);
      map.set(channelName, { channel, count: 1 });

      return channel;
    },
    [pusher]
  );

  const unsubscribe = useCallback(
    (channelName: string) => {
      if (!pusher) return;

      const map = channelsRef.current;
      const existing = map.get(channelName);
      if (!existing) return;

      existing.count--;
      console.log(
        `[Pusher][Channel] Decrement "${channelName}" (count=${existing.count})`
      );

      if (existing.count > 0) return;

      console.log(`[Pusher][Channel] UNSUBSCRIBE → ${channelName}`);
      pusher.unsubscribe(channelName);
      map.delete(channelName);
    },
    [pusher]
  );

  const toggleSubscription = useCallback(
    (channelName: string, enabled: boolean): Channel | null => {
      if (enabled) return subscribe(channelName);
      unsubscribe(channelName);
      return null;
    },
    [subscribe, unsubscribe]
  );

  const value = useMemo(
    () => ({
      pusher,
      isConnected,
      subscribe,
      unsubscribe,
      toggleSubscription,
    }),
    [pusher, isConnected, subscribe, unsubscribe, toggleSubscription]
  );

  return (
    <PusherContext.Provider value={value}>
      {children}
    </PusherContext.Provider>
  );
};
